import React, { useCallback, useState } from 'react'
import { Create, SimpleForm } from 'react-admin'
import { required, Record as RARecord } from 'ra-core'
import { Field } from 'react-final-form'
import {
  Checkbox,
  FormControlLabel,
  makeStyles,
  Theme,
} from '@material-ui/core'

import CustomTitle from 'src/adminPanel/screens/Title/Title'
import EntityPropertiesList from 'src/components/EntityPropertiesList'
import ConnectedInput from 'src/adminPanel/screens/inputs/ConnectedInput'
import RoleTemplateSelect from './RoleTemplatesSelect'
import CreateRoleToolbar from './CreateRoleToolbar'
import ConditionFormGroup from 'src/components/ConditionFormGroup'
import ReferencedInput from 'src/components/inputs/Referenced/ReferencedInput'

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  groupInput: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
}))

export type TValues = {
  roleTemplateCheckbox?: boolean
  roleTemplate?: RARecord
  group?: string
  name: string
  groups: string[]
  description: string
}

const CreateRole = ({ entity = {}, basePath, resource }): JSX.Element => {
  const classes = useStyles()
  const [groupAutocompleteError, setGroupAutocompleteError] = useState<
    string | undefined
  >(undefined)

  const handleGroupValidationError = useCallback(
    (error: string | undefined) => {
      setGroupAutocompleteError(error)
    },
    [],
  )

  const validateGroup = useCallback(
    (value, values) => {
      const raError = required()(value, values)
      if (raError !== undefined) return raError

      return groupAutocompleteError
    },
    [groupAutocompleteError],
  )

  return (
    <Create
      title={
        <CustomTitle
          entity={entity}
          record={null}
        />
      }
      basePath={basePath}
      resource={resource}
    >
      <SimpleForm
        redirect='edit'
        toolbar={<CreateRoleToolbar />}
      >
        {/* Returns an array because tables need them for columns to be displayed properly */}
        {/* @ts-expect-error */}
        <EntityPropertiesList
          entity={entity}
          render={({ propertyKey, property }) => (
            <ConnectedInput
              id={propertyKey}
              key={propertyKey}
              resource={resource}
              name={propertyKey}
              property={property}
              required={property.required}
            />
          )}
          childProps={{}}
        />
        <ConditionFormGroup
          when='roleTemplateCheckbox'
          is={true}
          type='checkbox'
          label={
            <Field
              className={classes.input}
              type='checkbox'
              initialValue={false}
              name='roleTemplateCheckbox'
            >
              {({ input }) => (
                <FormControlLabel
                  {...input}
                  label='Create from template'
                  control={<Checkbox color='primary' />}
                />
              )}
            </Field>
          }
        >
          <Field
            name='roleTemplate'
            validate={required()}
            component={RoleTemplateSelect}
          />
          <Field
            name='group'
            validate={validateGroup}
          >
            {({ input, meta }) => (
              <div className={classes.groupInput}>
                <ReferencedInput
                  {...input}
                  error={meta.error}
                  touched={meta.touched}
                  onValidationError={handleGroupValidationError}
                  property={{
                    reference: {
                      target: 'Group',
                      labelKeyPath: null,
                      targetLabelKeyPath: 'name',
                    },
                    displayedName: 'Group',
                  }}
                />
              </div>
            )}
          </Field>
        </ConditionFormGroup>
      </SimpleForm>
    </Create>
  )
}

export default CreateRole
